import type { ProtoLeagueTable, ProtoFixture } from "@catbento/shared";
import type { CatalystSnapshot } from "./snapshot.js";

// Pure lookups for the league group (Phase 02f). Grounded in renderers-leaguetable.js
// (leagueTables[competition][seasonCode]; default = the competition's newest captured
// season) + renderers-matchresults.js (matchResults[competition] = a flat fixtures array;
// default competition = the first key).
//
// The app asks by league id (ids-2); the snapshot's maps are still keyed by NAME
// ("FAWSL"), so these lookups turn the id back into the name inside (the same fork A1
// as players — the snapshot goes away with Track 2, so it isn't worth re-keying).

/**
 * A league (a Catalyst competition), as the app stores and passes it around: its DB id
 * (ids everywhere, track2-plan.md §8.6) — FAWSL = 37. Never its name: names are for
 * display, and the API asks by id. A number, like PlayerId, so the compiler finds every
 * place still passing a name.
 */
export type CompetitionId = number;

/** One league in a picker: the id the widget stores, and the label it shows. */
export interface LeagueChoice {
  id: CompetitionId;
  label: string;
}

/** A picker's value (always text) back to the id it was built from. Anything else is a
 *  bug — a name slipped in somewhere — so it throws. */
export function competitionIdOf(value: string): CompetitionId {
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) throw new Error(`Not a league id: "${value}"`);
  return id;
}

/**
 * Normalise a season code to a comparable number, exactly as
 * renderers-leaguetable.js does: a "5"-prefixed 5-digit European code ("52025" =
 * 2025/26) sorts as its later year + 1; a plain calendar-year code (NWSL) sorts
 * as itself. Lets the two formats be ordered consistently.
 */
export function seasonSortKey(code: string): number {
  const str = String(code);
  if (str.length === 5 && str.charAt(0) === "5") return parseInt(str.slice(1), 10) + 1;
  return parseInt(str, 10) || 0;
}

/** The leagues that have a league table, in the snapshot's own order. */
export function getLeagueCompetitions(data: CatalystSnapshot): LeagueChoice[] {
  return [...getLeagueIdNames(data)].map(([id, label]) => ({ id, label }));
}

/**
 * A competition's league table for a season. Default season = the competition's
 * newest captured one (a competition can't keep another's season on switch —
 * different captured ranges). Returns null if the competition or resolved season
 * isn't captured.
 */
export function getLeagueTable(
  data: CatalystSnapshot,
  competitionId: CompetitionId,
  season?: string
): ProtoLeagueTable | null {
  const competition = getLeagueIdNames(data).get(competitionId);
  const comp = competition ? data.leagueTables?.[competition] : undefined;
  if (!competition || !comp) return null;

  const seasonCodes = Object.keys(comp).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
  const seasonCode = season && comp[season] ? season : (seasonCodes[0] ?? "");
  const block = comp[seasonCode];
  if (!block) return null;

  return {
    competition,
    leagueCode: block.leagueCode,
    seasonCode,
    seasonCodes,
    matchCount: block.matchCount,
    table: block.table,
  };
}

/**
 * The snapshot's leagues by their DB id (each captured season's `leagueCode` is the
 * Catalyst competition id: FAWSL = 37, NWSL = 49), in its own order. The one id ↔ name map:
 * the snapshot lookups turn an id into the name they're keyed by, and live mode gives these
 * leagues the snapshot's labels. Throws if one league's seasons disagree — that
 * would be a broken snapshot, not something to guess around.
 */
export function getLeagueIdNames(data: CatalystSnapshot): Map<CompetitionId, string> {
  const out = new Map<CompetitionId, string>();
  for (const [name, seasons] of Object.entries(data.leagueTables ?? {})) {
    const ids = new Set(Object.values(seasons).map((b) => Number(b.leagueCode)));
    if (ids.size !== 1) throw new Error(`Snapshot league "${name}" has ${ids.size} league ids`);
    out.set([...ids][0] as number, name);
  }
  return out;
}

/**
 * The snapshot league named `name`, or null when the snapshot has no id for it. Only the
 * leagues with a table carry one (FAWSL, NWSL); a player's league can be another (Liga F,
 * Frauen Bundesliga — no id anywhere in the snapshot), which is a real gap, not an error.
 */
export function getLeagueIdOfName(data: CatalystSnapshot, name: string): CompetitionId | null {
  for (const [id, n] of getLeagueIdNames(data)) if (n === name) return id;
  return null;
}

/** The leagues that have match results, in the snapshot's own order. Each must also have a
 *  table (that's where its id comes from) — one without would be a broken snapshot. */
export function getMatchResultCompetitions(data: CatalystSnapshot): LeagueChoice[] {
  return Object.keys(data.matchResults ?? {}).map((label) => {
    const id = getLeagueIdOfName(data, label);
    if (id === null) throw new Error(`Snapshot match results for "${label}" have no league id`);
    return { id, label };
  });
}

/** A league's fixtures (flat, all captured seasons), or [] if none. */
export function getMatchResults(data: CatalystSnapshot, competitionId: CompetitionId): ProtoFixture[] {
  const competition = getLeagueIdNames(data).get(competitionId);
  if (!competition) return [];
  return data.matchResults?.[competition] ?? [];
}

/** Every competition's fixtures, keyed by competition — the Matches widget looks a
 *  match up by id across ALL of them (its own competition labels don't string-match
 *  matchResults' keys). {} if none captured. */
export function getAllMatchResults(data: CatalystSnapshot): Record<string, ProtoFixture[]> {
  return data.matchResults ?? {};
}
